import {
  Campaign,
  CampaignType_Enum as CampaignType,
  Status_Enum as CampaignStatus,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useGetCampaign } from '@frontend/api-bulk-messaging';
import { useHasBulkEmailAccess, useHasBulkTextAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { BulkMessageEditorProvider } from '../bulk-message-editor-provider';
import { BulkMessageEditorPage } from '../components/bulk-message-editor-page';
import { BulkMessageError } from '../components/bulk-message-error';
import { BulkMessageLoader } from '../components/bulk-message-loader';
import { BulkMessagePage } from '../components/bulk-message-page';
import { EmptyStateNoAccess } from '../components/empty-state-no-access';
import { useCampaignSave } from '../hooks/use-campaign-save';
import { EditorView } from '../types';

type Props = {
  campaignType: CampaignType;
  copyAudience?: boolean;
  seedCampaignId: string;
  view: EditorView;
};

// TODO: Implement copyAudience
export const CampaignDuplicate = ({
  campaignType,
  copyAudience: _copyAudience = false,
  seedCampaignId,
  view,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { selectedOrgId: orgId } = useAppScopeStore();
  const { lastSavedCampaign, ...editorActions } = useCampaignSave(campaignType);
  const { hasBulkEmailAccess } = useHasBulkEmailAccess();
  const { hasBulkTextAccess } = useHasBulkTextAccess();
  const hasAccess = campaignType === CampaignType.EMAIL ? hasBulkEmailAccess : hasBulkTextAccess;

  const {
    data: seedCampaign,
    isLoading,
    isError,
  } = useGetCampaign({ campaignId: seedCampaignId, orgId }, { enabled: hasAccess });

  const campaign: Campaign = {
    attachmentMediaIds: seedCampaign?.attachmentMediaIds,
    campaignId: '',
    currentStatus: CampaignStatus.UNSPECIFIED,
    locationIds: seedCampaign?.locationIds ?? [],
    messageTemplate: seedCampaign?.messageTemplate ?? '',
    sendDays: seedCampaign?.sendDays,
    sendPerDay: seedCampaign?.sendPerDay,
    subject: seedCampaign?.subject,
    startSendAt: '',
    templateJson: seedCampaign?.templateJson,
    thumbnailMediaId: seedCampaign?.thumbnailMediaId,
    thumbnailUrl: seedCampaign?.thumbnailUrl,
    title: seedCampaign?.title ? t('{{title}} (Copy)', { title: seedCampaign.title }) : '',
    type: campaignType,
  };

  if (!hasAccess) {
    return <EmptyStateNoAccess campaignType={campaignType} />;
  }

  // TODO: Show a specific 404 error message for a not found seed campaign instead of a generic error message
  if (isError || (seedCampaign && seedCampaign.type !== campaignType)) {
    const error =
      campaignType === CampaignType.EMAIL
        ? t('An error occurred while loading your email campaign.')
        : t('An error occurred while loading your text campaign.');
    return (
      <BulkMessagePage error={error}>
        <BulkMessageError heading={error} />
      </BulkMessagePage>
    );
  }

  if (isLoading) {
    return <BulkMessageLoader />;
  }

  return (
    <BulkMessageEditorProvider
      {...editorActions}
      campaignType={campaignType}
      initialCampaign={lastSavedCampaign ? lastSavedCampaign : campaign}
      view={view}
    >
      <BulkMessageEditorPage />
    </BulkMessageEditorProvider>
  );
};
